import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderKanban, Plus, Trash2, UserPlus } from 'lucide-react';
import {
  type CreateWorkgroupRequest,
  type MemberRole,
  type Workgroup,
  type WorkgroupMember,
  addMember,
  createWorkgroup,
  listMembers,
  listWorkgroups,
  removeMember,
} from '@/api/workgroups';
import { useDirectory } from '@/hooks/useDirectory';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';

const ROLES: MemberRole[] = ['owner', 'admin', 'member', 'guest'];

export function WorkgroupsPage() {
  const { t } = useTranslation();
  const dir = useDirectory();

  const [groups, setGroups] = useState<Workgroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listWorkgroups()
      .then((list) => {
        setGroups(list);
        setSelected((cur) => cur ?? list[0]?.id ?? null);
      })
      .catch((err) => setError(err?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const current = groups.find((g) => g.id === selected) ?? null;

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('workgroups.title')}
          </h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">
            {t('workgroups.subtitle')}
          </p>
        </div>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="workgroup-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('workgroups.new')}
        </Button>
      </header>

      {error && (
        <div className="mb-4 rounded-lg border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-4">
        <aside className="w-72 shrink-0 overflow-y-auto rounded-lg border bg-card" data-testid="workgroup-list">
          {loading && <p className="p-4 text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!loading && groups.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">{t('workgroups.empty')}</p>
          )}
          <ul className="divide-y divide-[var(--color-lop-border)]">
            {groups.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => setSelected(g.id)}
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-[var(--color-lop-bg-card-hi)] ${
                    selected === g.id ? 'bg-[var(--color-lop-bg-card-hi)]' : ''
                  }`}
                >
                  <FolderKanban className="h-4 w-4 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-[var(--color-lop-text-primary)]">
                      {g.name}
                    </span>
                  </span>
                  {g.isProject && (
                    <span className="rounded-full bg-[var(--color-lop-bg-card)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-lop-teal)]">
                      {t('workgroups.projectBadge')}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col rounded-lg border bg-card p-5">
          {!current && (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {t('workgroups.selectPrompt')}
            </div>
          )}
          {current && <WorkgroupDetail group={current} dir={dir} />}
        </section>
      </div>

      {showNew && (
        <NewWorkgroupModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            load();
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function WorkgroupDetail({
  group,
  dir,
}: {
  group: Workgroup;
  dir: ReturnType<typeof useDirectory>;
}) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<WorkgroupMember[]>([]);
  const [addUser, setAddUser] = useState('');
  const [addRole, setAddRole] = useState<MemberRole>('member');

  const loadMembers = useCallback(() => {
    listMembers(group.id)
      .then(setMembers)
      .catch(() => setMembers([]));
  }, [group.id]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const memberIds = new Set(members.map((m) => m.userId));
  const candidates = [...dir.users.values()].filter((u) => !memberIds.has(u.id));

  async function add() {
    if (!addUser) return;
    await addMember(group.id, addUser, addRole);
    setAddUser('');
    setAddRole('member');
    loadMembers();
  }

  async function remove(userId: string) {
    await removeMember(group.id, userId);
    loadMembers();
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-[var(--color-lop-text-primary)]">{group.name}</h2>
        {group.description && (
          <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">{group.description}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-2 text-xs text-[var(--color-lop-text-muted)]">
          {group.isProject && (
            <span className="rounded-full bg-[var(--color-lop-bg-card-hi)] px-2 py-0.5 text-[var(--color-lop-teal)]">
              {t('workgroups.projectBadge')}
            </span>
          )}
          {group.isScrum && (
            <span className="rounded-full bg-[var(--color-lop-bg-card-hi)] px-2 py-0.5">
              {t('workgroups.scrumBadge')}
            </span>
          )}
          {group.projectStart && (
            <span>
              {t('workgroups.dates', {
                start: group.projectStart.slice(0, 10),
                finish: group.projectFinish?.slice(0, 10) ?? '—',
              })}
            </span>
          )}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium text-[var(--color-lop-text-primary)]">
          {t('workgroups.members')}
        </h3>
        <ul className="mb-3 divide-y divide-[var(--color-lop-border)] rounded-lg border border-[var(--color-lop-border)]">
          {members.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">{t('workgroups.noMembers')}</li>
          )}
          {members.map((m) => (
            <li key={m.userId} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="text-[var(--color-lop-text-primary)]">{dir.nameFor(m.userId)}</span>
              <span className="flex items-center gap-2">
                <span className="rounded-full bg-[var(--color-lop-bg-card-hi)] px-2 py-0.5 text-[10px] uppercase text-[var(--color-lop-text-secondary)]">
                  {t(`workgroups.role.${m.role}`)}
                </span>
                {m.role !== 'owner' && (
                  <button
                    type="button"
                    onClick={() => remove(m.userId)}
                    className="text-[var(--color-lop-text-muted)] hover:text-red-500"
                    title={t('workgroups.removeMember')}
                    data-testid="workgroup-remove-member"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-end gap-2">
          <Select
            value={addUser}
            onChange={(e) => setAddUser(e.target.value)}
            className="flex-1"
            data-testid="workgroup-add-user"
          >
            <option value="">{t('workgroups.selectUser')}</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {dir.nameFor(u.id)}
              </option>
            ))}
          </Select>
          <Select value={addRole} onChange={(e) => setAddRole(e.target.value as MemberRole)} className="w-32">
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`workgroups.role.${r}`)}
              </option>
            ))}
          </Select>
          <Button size="sm" variant="outline" onClick={add} disabled={!addUser}>
            <UserPlus className="mr-1 h-4 w-4" />
            {t('workgroups.addMember')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NewWorkgroupModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const myId = useAuthStore((s) => s.user?.userId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isProject, setIsProject] = useState(false);
  const [isScrum, setIsScrum] = useState(false);
  const [projectStart, setProjectStart] = useState('');
  const [projectFinish, setProjectFinish] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const req: CreateWorkgroupRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        isProject,
        isScrum,
        ownerUserId: myId,
      };
      if (isProject && projectStart) req.projectStart = projectStart;
      if (isProject && projectFinish) req.projectFinish = projectFinish;
      const { id } = await createWorkgroup(req);
      onCreated(id);
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('workgroups.newModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('workgroups.newModal.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('workgroups.newModal.description')}
        </label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="mb-3"
        />
        <label className="mb-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isProject} onChange={(e) => setIsProject(e.target.checked)} />
          {t('workgroups.newModal.isProject')}
        </label>
        {isProject && (
          <div className="mb-2 grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
                {t('workgroups.newModal.start')}
              </label>
              <Input type="date" value={projectStart} onChange={(e) => setProjectStart(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
                {t('workgroups.newModal.finish')}
              </label>
              <Input type="date" value={projectFinish} onChange={(e) => setProjectFinish(e.target.value)} />
            </div>
          </div>
        )}
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isScrum} onChange={(e) => setIsScrum(e.target.checked)} />
          {t('workgroups.newModal.isScrum')}
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={create} disabled={submitting || !name.trim()} data-testid="workgroup-new-submit">
            {t('workgroups.newModal.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}
